import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { Search, Check, ChevronDown, Globe } from 'lucide-react';
import { timezones, cycleOptions, weekDays } from './constants';
import { InfoTooltip } from './InfoTooltip';

interface ScheduleSelectorProps {
  selectedDate: Date;
  selectedTime: string;
  selectedTimezone: string;
  userDefaultTimezone?: string | null;
  cycle: string;
  selectedDays: string[];
  minTime: string;
  onDateChange: (date: Date) => void;
  onTimeChange: (time: string) => void;
  onTimezoneChange: (timezone: string) => void;
  onCycleChange: (cycle: string) => void;
  onDaysChange: (days: string[]) => void;
}

export const ScheduleSelector: React.FC<ScheduleSelectorProps> = ({
  selectedDate,
  selectedTime,
  selectedTimezone,
  userDefaultTimezone,
  cycle,
  selectedDays,
  minTime,
  onDateChange,
  onTimeChange,
  onTimezoneChange,
  onCycleChange,
  onDaysChange,
}) => {
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsTimezoneOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const groupedTimezones = timezones.reduce((acc, tz) => {
    const region = tz.region || 'Inne';
    if (!acc[region]) acc[region] = [];
    acc[region].push(tz);
    return acc;
  }, {} as Record<string, typeof timezones>);

  const filteredGroupedTimezones = Object.entries(groupedTimezones).reduce((acc, [region, tzList]) => {
    const filtered = tzList.filter(tz =>
      tz.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tz.id.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) acc[region] = filtered;
    return acc;
  }, {} as Record<string, typeof timezones>);

  const selectedTz = timezones.find(tz => tz.id === selectedTimezone);
  const isUsingDefault = userDefaultTimezone && selectedTimezone === userDefaultTimezone;

  const handleTimezoneSelect = (tzId: string) => {
    onTimezoneChange(tzId);
    setIsTimezoneOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Data
          </label>
          <input
            type="date"
            value={format(selectedDate, 'yyyy-MM-dd')}
            min={format(new Date(), 'yyyy-MM-dd')}
            onChange={(e) => onDateChange(new Date(e.target.value))}
            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Godzina
            {format(new Date(), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd') && (
              <span className="ml-2 text-xs text-gray-500 font-normal">
                (min: {minTime})
              </span>
            )}
          </label>
          <input
            type="time"
            value={selectedTime}
            min={format(new Date(), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd') ? minTime : undefined}
            onChange={(e) => onTimeChange(e.target.value)}
            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div ref={dropdownRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Strefa czasowa
            {isUsingDefault && (
              <span className="text-xs text-green-600 font-normal">(domyslna)</span>
            )}
          </label>
          <button
            type="button"
            onClick={() => setIsTimezoneOpen(!isTimezoneOpen)}
            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between hover:border-blue-400 transition-colors"
          >
            <span className="truncate text-sm">{selectedTz?.label || selectedTimezone}</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isTimezoneOpen ? 'rotate-180' : ''}`} />
          </button>

          {isTimezoneOpen && (
            <div className="absolute z-50 w-full sm:w-80 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden right-0">
              <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Szukaj strefy czasowej..."
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
              </div>
              <div className="overflow-y-auto max-h-60">
                {userDefaultTimezone && !searchQuery && (
                  <div>
                    <div className="px-3 py-1.5 bg-green-50 text-xs font-semibold text-green-700 uppercase tracking-wider">
                      Twoja domyslna
                    </div>
                    <button
                      type="button"
                      onClick={() => handleTimezoneSelect(userDefaultTimezone)}
                      className={`w-full px-4 py-2 text-left hover:bg-green-50 flex items-center justify-between text-sm ${
                        selectedTimezone === userDefaultTimezone ? 'bg-green-50 text-green-700' : 'text-gray-700'
                      }`}
                    >
                      <span>{timezones.find(tz => tz.id === userDefaultTimezone)?.label || userDefaultTimezone}</span>
                      {selectedTimezone === userDefaultTimezone && <Check className="w-4 h-4 text-green-600" />}
                    </button>
                  </div>
                )}
                {Object.entries(filteredGroupedTimezones).map(([region, tzList]) => (
                  <div key={region}>
                    <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0">
                      {region}
                    </div>
                    {tzList.map((tz) => (
                      <button
                        key={tz.id}
                        type="button"
                        onClick={() => handleTimezoneSelect(tz.id)}
                        className={`w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center justify-between text-sm transition-colors ${
                          selectedTimezone === tz.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        <span>{tz.label}</span>
                        {selectedTimezone === tz.id && <Check className="w-4 h-4 text-blue-600" />}
                      </button>
                    ))}
                  </div>
                ))}
                {Object.keys(filteredGroupedTimezones).length === 0 && (
                  <div className="px-4 py-6 text-center text-gray-500 text-sm">
                    Nie znaleziono stref czasowych
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          Repeat Cycle
          <InfoTooltip text="Choose how often this post should be published: Once (one time only), Daily, Weekly (custom days), or Monthly (same date each month)" />
        </label>
        <div className="flex flex-wrap gap-2 mb-4">
          {cycleOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => onCycleChange(option.id)}
              className={`
                px-4 py-2 rounded-lg
                ${cycle === option.id ? 'bg-blue-600 text-white' : 'bg-gray-100'}
              `}
            >
              {option.label}
            </button>
          ))}
        </div>
        {cycle === 'custom' && (
          <div className="flex flex-wrap gap-2">
            {weekDays.map((day) => (
              <button
                key={day.id}
                onClick={() => {
                  onDaysChange(
                    selectedDays.includes(day.id)
                      ? selectedDays.filter(d => d !== day.id)
                      : [...selectedDays, day.id]
                  );
                }}
                className={`
                  px-3 py-1 rounded-lg text-sm
                  ${selectedDays.includes(day.id) ? 'bg-blue-600 text-white' : 'bg-gray-100'}
                `}
              >
                {day.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};